/**
 * Full-screen mobile navigation drawer for landing pages.
 * Focus trap, Escape / overlay close, body scroll lock.
 */
if (!customElements.get('landing-nav-drawer')) {
  class LandingNavDrawer extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLElement | null} */
  #previousFocus = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.panel = this.querySelector('[data-drawer-panel]');

    this.overlay?.addEventListener('click', () => this.close(), { signal });
    this.addEventListener('keydown', this.#onKeydown.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.isOpen) {
      document.body.classList.remove('landing-nav-drawer-open');
    }
  }

  get isOpen() {
    return this.hasAttribute('data-open');
  }

  /**
   * Opens the drawer and traps focus inside the panel.
   */
  open() {
    if (this.isOpen) return;

    this.#previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.setAttribute('data-open', '');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('landing-nav-drawer-open');
    this.#syncMenuToggle(true);

    const focusTarget = this.#getFocusableElements()[0];
    focusTarget?.focus();
  }

  /**
   * Closes the drawer and restores focus.
   */
  close() {
    if (!this.isOpen) return;

    this.removeAttribute('data-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('landing-nav-drawer-open');
    this.#syncMenuToggle(false);
    this.#previousFocus?.focus();
    this.#previousFocus = null;
  }

  /**
   * @param {boolean} expanded
   */
  #syncMenuToggle(expanded) {
    const toggle = document.querySelector(`[aria-controls="${this.id}"]`);
    if (!(toggle instanceof HTMLElement)) return;
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.setAttribute('aria-label', expanded ? 'Close menu' : 'Open menu');
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.#getFocusableElements();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getFocusableElements() {
    if (!this.panel) return [];

    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    return Array.from(this.panel.querySelectorAll(selector)).filter(
      (element) => element instanceof HTMLElement && !element.hasAttribute('hidden')
    );
  }
  }

  customElements.define('landing-nav-drawer', LandingNavDrawer);
}

if (!customElements.get('landing-header')) {
  class LandingHeader extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.menuToggle = this.querySelector('[data-menu-toggle]');
    this.drawer = this.querySelector('landing-nav-drawer');

    this.menuToggle?.addEventListener('click', () => {
      if (this.drawer?.isOpen) {
        this.drawer.close();
      } else {
        this.drawer?.open();
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }
  }

  customElements.define('landing-header', LandingHeader);
}
